// src/components/finance/LoanEligibilityModal.jsx
import React, { useState, useEffect } from 'react';
import '../../styles/LoanEligibilityModal.css';
import { loanService } from '../../services/loanService';
import PANStep from './steps/PANStep';
import ApplicantStep from './steps/ApplicantStep';
import AddressStep from './steps/AddressStep';
import EmploymentStep from './steps/EmploymentStep';
import FinancialStep from './steps/FinancialStep';
import OTPStep from './steps/OTPStep';
import EligibilityResult from './steps/EligibilityResult';

const LoanEligibilityModal = ({ isOpen, onClose, car }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [applicationId, setApplicationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const carId    = car?.id    || null;
  const carPrice = car?.price || 0;

  const [formData, setFormData] = useState({
    pan: '',
    full_name: '',
    email: '',
    dob: '',
    gender: '',
    mobile: '',
    pincode: '',
    city: '',
    state: '',
    address: '',
    residence_type: '',
    residence_duration_months: '',
    employment_type: '',
    employer_name: '',
    job_duration_months: '',
    total_experience_months: '',
    monthly_income: '',
    car_price: car?.price || 0,
    down_payment: 0,
    loan_amount_requested: car?.price || 0,
    tenure_requested: 60
  });

  const [otpData, setOtpData] = useState({ otpId: null, otp: '' });
  const [eligibilityResult, setEligibilityResult] = useState(null);

  // ✅ KEY FIX: Lock body scroll when modal is open, unlock on close
  useEffect(() => {
    if (isOpen) {
      // Calculate scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
      document.documentElement.style.removeProperty('--scrollbar-width');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
      document.documentElement.style.removeProperty('--scrollbar-width');
    };
  }, [isOpen]);

  const steps = [
    { number: 1, label: 'PAN Verification' },
    { number: 2, label: 'Basic Information' },
    { number: 3, label: 'Address Details' },
    { number: 4, label: 'Employment Type' },
    { number: 5, label: 'Financial Details' },
    { number: 6, label: 'Verification' },
  ];

  const handleNext = async (stepData) => {
    setLoading(true);
    setError(null);

    try {
      const updatedFormData = { ...formData, ...stepData };
      setFormData(updatedFormData);

      if (currentStep === 1) {
        const response = await loanService.createApplication(carId);
        setApplicationId(response.loan_application_id);
        setCurrentStep(2);
      } else if (currentStep === 2) {
        await loanService.submitApplicant(applicationId, {
          pan: formData.pan,
          full_name: stepData.full_name,
          email: stepData.email,
          dob: stepData.dob,
          gender: stepData.gender,
          mobile: stepData.mobile
        });
        setCurrentStep(3);
      } else if (currentStep === 3) {
        await loanService.submitAddress(applicationId, stepData);
        setCurrentStep(4);
      } else if (currentStep === 4) {
        await loanService.submitEmployment(applicationId, {
          employment_type: stepData.employment_type,
          employer_name: stepData.employer_name,
          monthly_income: stepData.monthly_income,
          job_duration_months: stepData.job_duration_months,
          total_experience_months: stepData.total_experience_months
        });
        setCurrentStep(5);
      } else if (currentStep === 5) {
        await loanService.submitFinancials(applicationId, { ...stepData, car_price: carPrice });
        const otpResponse = await loanService.sendOTP(applicationId, updatedFormData.mobile);
        setOtpData({ ...otpData, otpId: otpResponse.otp_id });
        setCurrentStep(6);
      } else if (currentStep === 6) {
        await loanService.verifyOTP(applicationId, otpData.otpId, stepData.otp);
        const result = await loanService.calculateEligibility(applicationId);
        setEligibilityResult(result);
        setCurrentStep(7);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  // ✅ Close on overlay click (but not modal itself)
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="loan-modal-overlay" onClick={handleOverlayClick}>
      <div className="loan-modal-container">

        {/* ── Header ── */}
        <div className="loan-modal-header">
          <div className="header-content">
            <div className="header-icon">
              <svg width="40" height="40" viewBox="0 0 40 40">
                <rect width="40" height="40" rx="10" fill="#FFD60A"/>
                <path d="M20 10L12 16V26H28V16L20 10Z" fill="#003566"/>
              </svg>
            </div>
            <div>
              <h2>Loan</h2>
              <p>Financing made possible for every car buyer</p>
            </div>
          </div>
          <button className="close-button" onClick={onClose} aria-label="Close modal">✕</button>
        </div>

        {/* ── Progress Steps (hide on result step) ── */}
        {currentStep < 7 && (
          <div className="progress-steps">
            {steps.map((step) => (
              <div
                key={step.number}
                className={`progress-step ${currentStep >= step.number ? 'active' : ''} ${currentStep === step.number ? 'current' : ''}`}
              >
                <div className="step-number">{step.number}</div>
                <div className="step-label">{step.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Error Banner ── */}
        {error && (
          <div className="error-banner">
            <span>⚠️ {error}</span>
          </div>
        )}

        {/* ── Step Content ── */}
        <div className="loan-modal-body">
          {currentStep === 1 && (
            <PANStep data={formData} onNext={handleNext} loading={loading} />
          )}
          {currentStep === 2 && (
            <ApplicantStep data={formData} onNext={handleNext} onPrevious={handlePrevious} loading={loading} />
          )}
          {currentStep === 3 && (
            <AddressStep data={formData} onNext={handleNext} onPrevious={handlePrevious} loading={loading} />
          )}
          {currentStep === 4 && (
            <EmploymentStep data={formData} onNext={handleNext} onPrevious={handlePrevious} loading={loading} />
          )}
          {currentStep === 5 && (
            <FinancialStep data={formData} car={car} onNext={handleNext} onPrevious={handlePrevious} loading={loading} />
          )}
          {currentStep === 6 && (
            <OTPStep
              mobile={formData.mobile}
              onNext={handleNext}
              onPrevious={handlePrevious}
              onResend={() => loanService.sendOTP(applicationId, formData.mobile)}
              loading={loading}
            />
          )}
          {currentStep === 7 && (
            <EligibilityResult result={eligibilityResult} car={car} onClose={onClose} />
          )}
        </div>

        {/* ── Footer ── */}
        <div className="loan-modal-footer">
          <div className="security-badge">
            <svg width="15" height="15" viewBox="0 0 16 16" fill="#10B981">
              <path d="M8 0L2 3v5c0 3.5 2.5 6.5 6 7 3.5-.5 6-3.5 6-7V3L8 0z"/>
            </svg>
            <span>Your data is 100% safe and secure</span>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LoanEligibilityModal;